class LeraNav extends HTMLElement {
  constructor() {
    super();

    this.wrapper = null;
    this.sectionHeader = null;
    this.toggle = null;
    this.mobilePanel = null;
    this.compactMenu = null;
    this.closeButtons = [];
    this.backdrop = null;
    this.isOpen = false;
    this.isCompact = false;
    this.mobileUserHasScrolled = false;
    this.lastMobileScrollY = null;
    this.compactMedia = window.matchMedia('(min-width: 990px)');
    this.scrollThreshold = 1;
    this.onScroll = this.onScroll.bind(this);
    this.onToggleClick = this.onToggleClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onBackdropClick = this.onBackdropClick.bind(this);
  }

  connectedCallback() {
    this.wrapper = this.closest('.header-wrapper');
    this.sectionHeader = document.querySelector('.section-header');
    this.toggle = this.querySelector('.lera-nav__toggle');
    this.mobilePanel = document.getElementById('lera-mobile-panel');
    this.compactMenu = document.getElementById('lera-compact-menu');
    this.backdrop = document.getElementById('lera-mobile-backdrop');
    this.closeButtons = document.querySelectorAll('.lera-nav__close');

    if (!this.toggle || !this.wrapper) return;

    this.toggle.addEventListener('click', this.onToggleClick);
    this.backdrop?.addEventListener('click', this.onBackdropClick);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.onCloseClick));
    document.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    this.compactMedia.addEventListener('change', this.onMediaChange);

    this.initMobileHeader();
    this.onMediaChange();
    this.onScroll();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggleClick);
    this.backdrop?.removeEventListener('click', this.onBackdropClick);
    this.closeButtons.forEach((button) => button.removeEventListener('click', this.onCloseClick));
    document.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.compactMedia.removeEventListener('change', this.onMediaChange);
    this.clearStickyState();
    this.clearMobileFixedHeader();
  }

  isDesktop() {
    return this.compactMedia.matches;
  }

  onMediaChange() {
    this.close();
    if (!this.isDesktop()) {
      this.clearStickyState();
      this.initMobileHeader();
    } else {
      this.clearMobileFixedHeader();
    }
    this.onScroll();
  }

  onResize() {
    if (this.isCompact && this.isDesktop()) {
      this.updateStickyPlaceholder();
    } else if (!this.isDesktop()) {
      this.syncMobileStickyHeader();
    }
  }

  onScroll() {
    if (!this.wrapper) return;

    if (!this.isDesktop()) {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      if (this.lastMobileScrollY !== null && scrollY !== this.lastMobileScrollY) {
        this.mobileUserHasScrolled = true;
      }
      this.lastMobileScrollY = scrollY;
    }

    const shouldCompact = this.isDesktop() && window.scrollY > this.scrollThreshold;

    if (shouldCompact !== this.isCompact) {
      this.wrapper.classList.toggle('lera-compact-active', shouldCompact);
      document.body.classList.toggle('lera-desktop-sticky-active', shouldCompact);
      this.isCompact = shouldCompact;

      if (shouldCompact) {
        this.syncStickyHeader();
      } else {
        this.clearStickyState();
        this.close();
      }
    } else if (shouldCompact) {
      this.syncStickyHeader();
    }

    this.syncMobileStickyHeader();
  }

  initMobileHeader() {
    if (this.isDesktop()) return;

    this.mobileUserHasScrolled = false;
    this.lastMobileScrollY = window.scrollY || document.documentElement.scrollTop;
    this.applyNaturalMobileHeader();
  }

  getAnnouncementBottom() {
    const announcements = document.querySelectorAll(
      '.shopify-section-group-header-group > .shopify-section:not(.section-header)'
    );
    let bottom = 0;

    announcements.forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > bottom) {
        bottom = rect.bottom;
      }
    });

    return bottom;
  }

  applyNaturalMobileHeader() {
    if (!this.sectionHeader || !this.wrapper) return;

    document.body.classList.remove('lera-mobile-fixed-header-active');
    this.wrapper.style.top = '';
    this.sectionHeader.style.minHeight = '';
    this.sectionHeader.classList.remove('shopify-section-header-sticky', 'lera-mobile-header-sticky');
    this.sectionHeader.classList.remove('shopify-section-header-hidden');
  }

  syncMobileStickyHeader() {
    if (this.isDesktop() || !this.sectionHeader || !this.wrapper) return;

    this.sectionHeader.classList.remove('shopify-section-header-hidden');

    const scrollY = window.scrollY || document.documentElement.scrollTop;

    if (scrollY <= 2) {
      this.mobileUserHasScrolled = false;
      this.lastMobileScrollY = scrollY;
      this.applyNaturalMobileHeader();
      return;
    }

    const announcementScrolledPast = this.getAnnouncementBottom() <= 0;

    if (!this.mobileUserHasScrolled || !announcementScrolledPast) {
      this.applyNaturalMobileHeader();
      return;
    }

    document.body.classList.add('lera-mobile-fixed-header-active');
    this.sectionHeader.classList.add('shopify-section-header-sticky', 'lera-mobile-header-sticky');
    this.wrapper.style.top = '0px';

    const height = this.wrapper.offsetHeight;
    this.sectionHeader.style.minHeight = `${height}px`;
    document.documentElement.style.setProperty('--header-height', `${height}px`);
  }

  clearMobileFixedHeader() {
    this.mobileUserHasScrolled = false;
    this.lastMobileScrollY = null;
    this.applyNaturalMobileHeader();
  }

  syncStickyHeader() {
    if (!this.isDesktop() || !this.sectionHeader) return;

    this.sectionHeader.classList.add('shopify-section-header-sticky', 'lera-sticky-visible');
    this.sectionHeader.classList.remove('shopify-section-header-hidden');

    requestAnimationFrame(() => this.updateStickyPlaceholder());
  }

  updateStickyPlaceholder() {
    if (!this.wrapper || !this.sectionHeader || !this.isCompact || !this.isDesktop()) return;

    const height = this.wrapper.offsetHeight;
    this.sectionHeader.style.minHeight = `${height}px`;
    document.documentElement.style.setProperty('--header-height', `${height}px`);
  }

  clearStickyState() {
    document.body.classList.remove('lera-desktop-sticky-active');

    if (this.sectionHeader) {
      this.sectionHeader.classList.remove('lera-sticky-visible');
      this.sectionHeader.style.minHeight = '';
    }
  }

  onToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.isOpen ? this.close() : this.open();
  }

  onCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  onBackdropClick(event) {
    event.preventDefault();
    if (!this.isDesktop() && this.isOpen) {
      this.close();
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE' || !this.isOpen) return;
    this.close();
    this.toggle?.focus();
  }

  open() {
    const useCompactMenu =
      this.isDesktop() && (this.isCompact || this.wrapper.classList.contains('lera-compact-active'));
    const panel = useCompactMenu ? this.compactMenu : this.mobilePanel;

    if (!panel || !this.toggle) return;

    if (this.isDesktop() && panel === this.mobilePanel) return;

    this.isOpen = true;
    this.toggle.setAttribute('aria-expanded', 'true');
    this.wrapper.classList.add('lera-nav-open');

    if (useCompactMenu) {
      panel.removeAttribute('hidden');
      requestAnimationFrame(() => {
        panel.classList.add('lera-nav-panel--open');
        this.updateStickyPlaceholder();
      });
    } else {
      panel.hidden = false;
      panel.classList.add('lera-nav-panel--open');
      document.body.classList.add('lera-mobile-panel-open');
      this.showMobileBackdrop();
      requestAnimationFrame(() => this.updateStickyPlaceholder());
    }
  }

  showMobileBackdrop() {
    if (!this.backdrop || this.isDesktop()) return;

    this.backdrop.hidden = false;
    this.backdrop.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      this.backdrop.classList.add('lera-mobile-backdrop--visible');
    });
  }

  hideMobileBackdrop() {
    if (!this.backdrop || this.isDesktop()) return;

    this.backdrop.classList.remove('lera-mobile-backdrop--visible');
    this.backdrop.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      if (!this.isOpen) {
        this.backdrop.hidden = true;
      }
    }, 250);
  }

  close() {
    if (!this.toggle) return;

    this.isOpen = false;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.wrapper?.classList.remove('lera-nav-open');

    const closeCompactOnDesktop =
      this.isDesktop() &&
      this.compactMenu &&
      this.compactMenu.classList.contains('lera-nav-panel--open');

    if (closeCompactOnDesktop) {
      this.compactMenu.classList.remove('lera-nav-panel--open');
      window.setTimeout(() => {
        if (!this.isOpen) {
          this.compactMenu.setAttribute('hidden', '');
        }
        this.updateStickyPlaceholder();
      }, 300);
    } else {
      [this.mobilePanel, this.compactMenu].forEach((panel) => {
        if (!panel) return;
        panel.classList.remove('lera-nav-panel--open');
        panel.hidden = true;
      });
      this.hideMobileBackdrop();
    }

    document.body.classList.remove('lera-mobile-panel-open');

    if (!closeCompactOnDesktop) {
      requestAnimationFrame(() => this.updateStickyPlaceholder());
    }
  }
}

customElements.define('lera-nav', LeraNav);
